import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { jsPDF } from 'jspdf';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  PhotoLibrary as GalleryIcon,
  Close as CloseIcon,
  FlipCameraIos as FlipCameraIcon,
  Check as CheckIcon,
  Replay as RetakeIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import './DocumentCameraCapture.css';

interface DocumentCameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  open: boolean;
}

const DocumentCameraCapture: React.FC<DocumentCameraCaptureProps> = ({
  onCapture,
  onClose,
  open
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 1159px)');
  
  const [mode, setMode] = useState<'select' | 'camera' | 'preview'>('select');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Stop camera stream when closing or unmounting
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      stopCamera();
      setMode('select');
      setCapturedImage(null);
      setCapturedFile(null);
      setError(null);
    }
  }, [open, stopCamera]);

  // Start camera stream
  const startCamera = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      
      setMode('camera');
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(t('documentCamera.cameraAccessError'));
      // Fallback to file input for camera
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    } finally {
      setLoading(false);
    }
  };

  // Flip camera (front/back)
  const flipCamera = async () => {
    stopCamera();
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);
    
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Error flipping camera:', err);
      setError(t('documentCamera.cameraFlipError'));
    }
  };

  // Capture photo from video stream - crops to document frame area
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    // Get actual video dimensions
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    // Get displayed video dimensions
    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;
    
    // Calculate aspect ratios
    const videoAspect = videoWidth / videoHeight;
    const displayAspect = displayWidth / displayHeight;
    
    // Calculate the visible portion of the video (accounting for object-fit: cover)
    let visibleVideoWidth = videoWidth;
    let visibleVideoHeight = videoHeight;
    let videoOffsetX = 0;
    let videoOffsetY = 0;
    
    if (videoAspect > displayAspect) {
      // Video is wider - sides are cropped
      visibleVideoWidth = videoHeight * displayAspect;
      videoOffsetX = (videoWidth - visibleVideoWidth) / 2;
    } else {
      // Video is taller - top/bottom are cropped
      visibleVideoHeight = videoWidth / displayAspect;
      videoOffsetY = (videoHeight - visibleVideoHeight) / 2;
    }
    
    // Document frame dimensions in the display (CSS values)
    // Frame is 75% width, max 340px, with 8.5:11 aspect ratio
    const frameWidthPercent = 0.75;
    const maxFrameWidth = 340;
    
    let frameDisplayWidth = Math.min(displayWidth * frameWidthPercent, maxFrameWidth);
    let frameDisplayHeight = frameDisplayWidth * (11 / 8.5); // 8.5:11 aspect ratio
    
    // If frame height exceeds display height, constrain by height
    if (frameDisplayHeight > displayHeight * 0.85) {
      frameDisplayHeight = displayHeight * 0.85;
      frameDisplayWidth = frameDisplayHeight * (8.5 / 11);
    }
    
    // Frame is centered in the display
    const frameDisplayX = (displayWidth - frameDisplayWidth) / 2;
    const frameDisplayY = (displayHeight - frameDisplayHeight) / 2;
    
    // Map display coordinates to actual video coordinates
    const scaleX = visibleVideoWidth / displayWidth;
    const scaleY = visibleVideoHeight / displayHeight;
    
    const cropX = videoOffsetX + (frameDisplayX * scaleX);
    const cropY = videoOffsetY + (frameDisplayY * scaleY);
    const cropWidth = frameDisplayWidth * scaleX;
    const cropHeight = frameDisplayHeight * scaleY;
    
    // Set canvas to the cropped document dimensions (maintain good resolution)
    // Use a reasonable output size that maintains quality
    const outputWidth = Math.min(cropWidth, 1700); // Max ~8.5" at 200dpi
    const outputHeight = outputWidth * (11 / 8.5);
    
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    
    // Draw only the cropped portion of the video to the canvas
    context.drawImage(
      video,
      cropX, cropY, cropWidth, cropHeight,  // Source rectangle (from video)
      0, 0, outputWidth, outputHeight        // Destination rectangle (on canvas)
    );
    
    // Get the image data for preview
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageDataUrl);
    
    // Convert to PDF (8.5 x 11 inches)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter' // 8.5 x 11 inches
    });
    
    // Add the image to fill the entire page
    // Letter size is 8.5 x 11 inches
    pdf.addImage(imageDataUrl, 'JPEG', 0, 0, 8.5, 11);
    
    // Convert PDF to blob and create file
    const pdfBlob = pdf.output('blob');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const pdfFile = new File([pdfBlob], `document-scan-${timestamp}.pdf`, { type: 'application/pdf' });
    
    setCapturedFile(pdfFile);
    stopCamera();
    setMode('preview');
  };

  // Convert image data URL to PDF file
  const convertImageToPdf = (imageDataUrl: string): File => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter' // 8.5 x 11 inches
    });
    
    // Create an image to get dimensions for proper scaling
    const img = new Image();
    img.src = imageDataUrl;
    
    // Calculate dimensions to fit 8.5x11 while maintaining aspect ratio
    const pageWidth = 8.5;
    const pageHeight = 11;
    const imgAspect = img.width / img.height;
    const pageAspect = pageWidth / pageHeight;
    
    let imgWidth = pageWidth;
    let imgHeight = pageHeight;
    let offsetX = 0;
    let offsetY = 0;
    
    if (imgAspect > pageAspect) {
      // Image is wider - fit to width, center vertically
      imgHeight = pageWidth / imgAspect;
      offsetY = (pageHeight - imgHeight) / 2;
    } else {
      // Image is taller - fit to height, center horizontally
      imgWidth = pageHeight * imgAspect;
      offsetX = (pageWidth - imgWidth) / 2;
    }
    
    // Add the image centered on the page
    pdf.addImage(imageDataUrl, 'JPEG', offsetX, offsetY, imgWidth, imgHeight);
    
    // Convert PDF to blob and create file
    const pdfBlob = pdf.output('blob');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return new File([pdfBlob], `document-scan-${timestamp}.pdf`, { type: 'application/pdf' });
  };

  // Handle file selection from gallery
  const handleGallerySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if it's already a PDF
      if (file.type === 'application/pdf') {
        setCapturedFile(file);
        // For PDF preview, we'll just show a placeholder or the first page
        setCapturedImage('/pdf-placeholder.png'); // You could create a PDF icon placeholder
        setMode('preview');
      } else {
        // It's an image - convert to PDF
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageDataUrl = e.target?.result as string;
          setCapturedImage(imageDataUrl);
          
          // Convert image to PDF
          const pdfFile = convertImageToPdf(imageDataUrl);
          setCapturedFile(pdfFile);
          setMode('preview');
        };
        reader.readAsDataURL(file);
      }
    }
    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  // Handle camera input (fallback for devices where getUserMedia doesn't work)
  const handleCameraInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target?.result as string;
        setCapturedImage(imageDataUrl);
        
        // Convert image to PDF
        const pdfFile = convertImageToPdf(imageDataUrl);
        setCapturedFile(pdfFile);
        setMode('preview');
      };
      reader.readAsDataURL(file);
    }
    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };

  // Confirm and return captured image
  const confirmCapture = () => {
    if (capturedFile) {
      onCapture(capturedFile);
      onClose();
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    setCapturedFile(null);
    setMode('select');
  };

  // Open gallery
  const openGallery = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Selection mode - choose camera or gallery
  const renderSelectionMode = () => (
    <Box className="camera-selection-container">
      <Typography variant="h6" className="camera-selection-title">
        {t('documentCamera.selectMethod')}
      </Typography>
      <Typography variant="body2" className="camera-selection-subtitle">
        {t('documentCamera.selectMethodDescription')}
      </Typography>
      
      <Box className="camera-selection-buttons">
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CameraIcon />}
          onClick={startCamera}
          className="camera-option-btn"
          disabled={loading}
          aria-label={t('documentCamera.takePhoto')}
        >
          {t('documentCamera.takePhoto')}
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<GalleryIcon />}
          onClick={openGallery}
          className="gallery-option-btn"
          aria-label={t('documentCamera.chooseFromGallery')}
        >
          {t('documentCamera.chooseFromGallery')}
        </Button>
      </Box>
      
      {error && (
        <Typography variant="body2" color="error" className="camera-error">
          {error}
        </Typography>
      )}
      
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleGallerySelect}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraInput}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
    </Box>
  );

  // Camera mode - live viewfinder with document overlay
  const renderCameraMode = () => (
    <Box className="camera-viewfinder-container">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="camera-video"
        aria-label={t('documentCamera.cameraViewfinder')}
      />
      
      {/* Document alignment overlay */}
      <div className="document-overlay" aria-hidden="true">
        <div className="document-frame">
          <div className="corner top-left" />
          <div className="corner top-right" />
          <div className="corner bottom-left" />
          <div className="corner bottom-right" />
          <div className="guide-text">
            {t('documentCamera.alignDocument')}
          </div>
        </div>
      </div>
      
      {/* Camera controls */}
      <Box className="camera-controls">
        <IconButton
          onClick={flipCamera}
          className="flip-camera-btn"
          aria-label={t('documentCamera.flipCamera')}
        >
          <FlipCameraIcon />
        </IconButton>
        
        <IconButton
          onClick={capturePhoto}
          className="capture-btn"
          aria-label={t('documentCamera.capture')}
        >
          <CameraIcon />
        </IconButton>
        
        <IconButton
          onClick={() => {
            stopCamera();
            setMode('select');
          }}
          className="cancel-camera-btn"
          aria-label={t('common.cancel')}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />
    </Box>
  );

  // Preview mode - review captured image
  const renderPreviewMode = () => (
    <Box className="camera-preview-container">
      {capturedImage && (
        <img
          src={capturedImage}
          alt={t('documentCamera.capturedDocument')}
          className="captured-image"
        />
      )}
      
      {/* PDF indicator */}
      <Box className="pdf-indicator">
        <PdfIcon className="pdf-icon" />
        <Typography variant="body2">
          {t('documentCamera.convertedToPdf')}
        </Typography>
      </Box>
      
      <Box className="preview-controls">
        <Button
          variant="outlined"
          startIcon={<RetakeIcon />}
          onClick={retakePhoto}
          className="retake-btn"
          aria-label={t('documentCamera.retake')}
        >
          {t('documentCamera.retake')}
        </Button>
        
        <Button
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={confirmCapture}
          className="confirm-btn"
          aria-label={t('documentCamera.usePhoto')}
        >
          {t('documentCamera.usePhoto')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile && mode === 'camera'}
      className={`document-camera-dialog ${mode === 'camera' ? 'fullscreen-camera' : ''}`}
    >
      <DialogTitle className="camera-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {mode === 'select' && t('documentCamera.title')}
            {mode === 'camera' && t('documentCamera.scanDocument')}
            {mode === 'preview' && t('documentCamera.reviewPhoto')}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label={t('common.close')}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent className={`camera-dialog-content ${mode}`}>
        {mode === 'select' && renderSelectionMode()}
        {mode === 'camera' && renderCameraMode()}
        {mode === 'preview' && renderPreviewMode()}
      </DialogContent>
      
      {mode === 'select' && (
        <DialogActions>
          <Button onClick={onClose} aria-label={t('common.cancel')}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DocumentCameraCapture;

